<template>
    <div class="server-filter flex relative w-full" :class="{'p-0': isFull}">
        <div class="buttons flex z-10" v-if="!isFull">
            <div class="p-3 cursor-pointer" @click="activeFilters = !activeFilters">
                <vs-icon v-tooltip="activeFilters ? $t('tooltip.Show') : $t('tooltip.Hide')" icon-pack="feather"
                         icon="icon-chevron-down"/>
            </div>
            <div class="p-3 cursor-pointer" @click="refreshFilter()">
                <vs-icon v-tooltip="$t('tooltip.Reset_filter')" icon-pack="feather" icon="icon-rotate-cw"/>
            </div>
        </div>
        <div class="w-full" :class="{activeFilters: activeFilters}">
            <div v-if="!isFull" class="server-filter__header mb-4">{{ $t('global.Filters') }}</div>
            <div v-if="isMounted" class="server-filter__container">
                <vs-row class="flex flex-wrap" v-if="Object.keys(filters).length">
                    <vs-col v-for="(value, name) of filters.date"
                            :key="name"
                            vs-w="12"
                            :vs-sm="isFull ? 12 : 6"
                            :vs-lg="isFull ? 12 : 3"
                            vs-xs="12"
                    >
                        <div class="m-2">
                            <label class="text-sm opacity-75">{{ $t(value.label) }}</label>
                            <DatePicker :date="value" :direction="value.direction" @select="selectDate"/>
                        </div>
                    </vs-col>
                    <vs-col v-for="(value, key) of filters.allFiltersByDate"
                            :key="key"
                            vs-w="12"
                            :vs-sm="isFull ? 12 : 6"
                            :vs-lg="isFull ? 12 : 3"
                            vs-xs="12">
                        <div class="m-2">
                            <label class="text-sm opacity-75">{{ $t(`server_filter.${key}`) }}</label>
                            <v-select
                                class="w-full"
                                :clear-search-on-select="true"
                                :closeOnSelect="false"
                                :multiple="true"
                                :options="value"
                                :reduce="item => item['id'] || item['name']"
                                :selectable="item => item.disabled"
                                v-model="selectedFilters.fields[key]"
                                :placeholder="$t(`server_filter.${key}`)"
                                label="name"
                                @option:selecting="selectedFilter($event)"
                                @option:deselected="deselectedFilter($event, key)"
                            />
                        </div>
                    </vs-col>
                </vs-row>
                <div class="w-full" v-if="!isFull">
                    <div class="flex justify-end mt-4 items-center">
                        <vs-button class="mr-2" color="primary" @click.once="submitFilter" type="filled">
                            {{ $t('buttons.Filter') }}
                        </vs-button>
                    </div>
                </div>
            </div>
            <div v-else class="text-center">
                <c-loader color="rgba(var(--vs-primary),1)"></c-loader>
            </div>
        </div>
    </div>
</template>

<script>
import vSelect from 'vue-select'
import DatePicker from '@/components/date-picker/Picker'
import {sorting} from '@/utils/mixins'
import ErrorHandler from "../../utils/errorHandler";

export default {
    name: 'ServerFilter',
    components: {
        vSelect,
        DatePicker
    },
    props: {
        isMounted: Boolean,
        filterData: {
            type: Object,
            default() {
                return {}
            }
        },
        isFull: {
            type: Boolean,
            default: false
        }
    },
    mixins: [sorting],
    data() {
        return {
            activeFilters: false,
            filters: {},
            selectedFilters: {
                date: {},
                fields: {}
            }
        }
    },
    watch: {
        'filterData.allFiltersByDate'(val) {
            this.initFilters(val)
        },

        'filterData.filtersBasedOnFilters'(value) {
            const data = {}

            Object.keys(this.filters.allFiltersByDate).map(key => {
                if (this.filterData.fieldsWithDependencies[key] && value[key]) {
                    data[key] = this.filterData.allFiltersByDate[key].map(item => {
                        value[key].map(a => {
                            if (a.name === item.name) {
                                item = a
                            }
                        })

                        return item
                    })
                } else {
                    if (this.filterData.allFiltersByDate[key]) {
                        data[key] = this.filterData.allFiltersByDate[key]
                    }
                }

                if (Object.values(this.filterData.fieldsWithDependencies).some(item => item === key)) {
                    const dep = {
                        integrations: "advertisers",
                        affiliates: "teams"
                    }

                    const selected = this.selectedFilters.fields[dep[key]]

                    if (selected) {
                        if (!data[key]) data[key] = []
                        data[dep[key]].map(item => {
                            if (selected.includes(item.id)) {
                                data[key].push(...item[key])
                            }
                        })
                    }
                }
            })

            this.initFilters(data)
        }
    },
    methods: {
        initFilters(filters) {
            const copyFilters = {...filters}
            for (const key in copyFilters) {
                copyFilters[key] = copyFilters[key].map(filter => {
                    if (this.filterData.fieldsWithDependencies[key]) {
                        const nameDependency = this.filterData.fieldsWithDependencies[key]
                        const data = {
                            name: filter.name,
                            dependency: {
                                name: nameDependency,
                                value: filter[nameDependency]
                            },
                            disabled: this.getSelectable(key, filter.name, 'name')
                        }

                        if (filter.id) data.id = filter.id

                        return data
                    } else {
                        const data = {
                            name: filter.name || filter,
                            disabled: this.getSelectable(key, filter.name || filter)
                        }

                        if (filter.id) data.id = filter.id

                        return data
                    }
                })

                this.sortByAlphabet(copyFilters[key], 'name')
                copyFilters[key].sort((a, b) => (a.disabled === b.disabled) ? 0 : a.disabled ? -1 : 1)
            }
            this.filters.allFiltersByDate = copyFilters
        },

        selectedFilter(option) {
            const dependency = option.dependency
            if (dependency) {
                if (!this.filters.allFiltersByDate[dependency.name]) {
                    this.filters.allFiltersByDate[dependency.name] = option.dependency.value.map(filter => {
                        return {
                            name: filter.name || filter,
                            id: filter.id,
                            disabled: true
                        }
                    })
                } else {
                    option.dependency.value.forEach(item => {
                        if (!this.filters.allFiltersByDate[dependency.name].some(el => el.name === item)) {
                            this.filters.allFiltersByDate[dependency.name] = [
                                ...this.filters.allFiltersByDate[dependency.name],
                                {name: item.name || item, id: item.id, disabled: true}
                            ]
                        }
                    })
                }
            }
        },

        deselectedFilter(option, key) {
            try {
                if (option.dependency) {
                    const dependency = option.dependency
                    if (this.filters.allFiltersByDate[dependency.name]) {
                        const uniqueDependencies = new Set()

                        this.selectedFilters.fields[key].forEach(item => {
                            let selectedFilter = {}

                            if (typeof item === 'number') {
                                selectedFilter = this.filters.allFiltersByDate[key].find(filter => filter.id === item)
                            } else {
                                selectedFilter = this.filters.allFiltersByDate[key].find(filter => filter.name === item)
                            }

                            selectedFilter.dependency.value.forEach(dependency => uniqueDependencies.add(dependency))
                        })

                        this.filters.allFiltersByDate[dependency.name] = [...uniqueDependencies].map(dependency => {
                            const data = {
                                name: dependency.name || dependency,
                                disabled: true
                            }

                            if (dependency.id) data.id = dependency.id

                            return data
                        })
                    }

                    if (this.selectedFilters.fields[dependency.name]) {
                        this.selectedFilters.fields[dependency.name] = this.selectedFilters.fields[dependency.name].filter(item => this.filters.allFiltersByDate[dependency.name].some(el => el.name === item))

                        if (!this.selectedFilters.fields[dependency.name].length) {
                            delete this.selectedFilters.fields[dependency.name]
                        }
                    }

                    if (this.filters.allFiltersByDate[dependency.name] && !this.filters.allFiltersByDate[dependency.name].length) {
                        delete this.filters.allFiltersByDate[dependency.name]
                    }
                }

                if (!this.selectedFilters.fields[key].length) {
                    this.filters.allFiltersByDate[key] = this.filters.allFiltersByDate[key].map(filter => {
                        const newElement = {
                            name: filter.name || filter,
                            disabled: true
                        }

                        if (filter.id) newElement.id = filter.id

                        if (filter.dependency) {
                            newElement.dependency = filter.dependency
                        }
                        return newElement
                    })
                    delete this.selectedFilters.fields[key]
                }
            } catch (e) {
                console.log(e)
            }
        },

        getSelectable(key, item, nameProperty) {
            if (this.filterData.filtersBasedOnFilters[key]) {
                return this.filterData.filtersBasedOnFilters[key].some(el => {
                    return nameProperty ? el[nameProperty] === item : (el.name || el) === item
                })
            } else {
                return true
            }
        },

        selectDate(date) {
            this.filters.date[date.direction].startDate = date.startDate
            this.filters.date[date.direction].endDate = date.endDate

            Object.keys(this.filters.date).forEach(key => {
                if (key !== date.direction) {
                    this.filters.date[key].startDate = this.filterData.defaultDate.startDate
                    this.filters.date[key].endDate = this.filterData.defaultDate.endDate
                }
            })

            this.selectedFilters.date.selectedDirection = date.direction
        },

        deepCopyFilterData() {
            this.filters = JSON.parse(JSON.stringify(this.filterData))
            this.initFilters(this.filters.allFiltersByDate)
        },

        refreshFilter() {
            this.deepCopyFilterData()
            this.selectedFilters.date = {}
            this.selectedFilters.fields = {}
            this.submitFilter()
        },

        submitFilter() {
            this.selectedFilters.date = {...this.selectedFilters.date, ...this.filters.date}
            this.$emit('selectedFilters', this.selectedFilters)
        }
    },

    mounted() {
        this.deepCopyFilterData()
    }
}
</script>

<style lang="scss">

.server-filter {
    background: #ffffff;
    padding: 20px;
    border-radius: 0.5rem;
    margin-bottom: 15px;

    &__header {
        font-size: 19px;
        line-height: 17px;
        color: #000000;

    }

    &__container {
        height: auto;
        transition: all .25s ease;
    }

    .vs__dropdown-option--selected {
        display: none !important;
    }

    .vs__dropdown-option--disabled {
        background-color: #f6f6f6;
        color: rgba(0, 0, 0, 0.28) !important;
    }

    .v-select .vs__selected {
        word-break: break-all;
    }

}

.activeFilters {
    .server-filter {
        &__header {
            margin-bottom: 0 !important;
        }

        &__container {
            height: 0;
            transition: all .25s ease;
            overflow: hidden;
        }
    }
}


.buttons {
    position: absolute;
    top: 0;
    right: 0;
}

.content-tr-expand {
    justify-content: left !important;
}
</style>
